import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noor/ui';
import { toast, type ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { useCouriers } from '@/entities/courier';
import { useReOfferCourier } from '../model/use-order-actions';

/**
 * Re-offer modal: pick a SPECIFIC courier to re-assign the order to (distinct
 * from blind re-order, which lets dispatch choose). Mirrors cpanel's
 * `reOfferCourier` flow. Lists couriers (id + name + phone); on confirm it calls
 * the re-offer mutation and closes.
 */
export function ReOfferModal({ payload, close }: ModalRenderProps<'re-offer'>) {
  const { t } = useTranslation('orders');
  const { data, isLoading } = useCouriers();
  const mutation = useReOfferCourier();

  const [courierId, setCourierId] = useState('');

  const couriers = data?.data ?? [];
  const hasCouriers = couriers.length > 0;

  const onConfirm = () => {
    if (!courierId) {
      toast.warning(t('detail.reofferPick'));
      return;
    }
    mutation.mutate({ orderId: payload.orderId, courierId }, { onSuccess: close });
  };

  return (
    <Can permission="couriers.manage_order">
      <Modal
        title={t('detail.reofferTitle')}
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              size="modal"
              shape="rounded"
              onClick={close}
              disabled={mutation.isPending}
            >
              {t('confirm.cancel')}
            </Button>
            <Button
              variant="dark"
              size="modal"
              shape="rounded"
              onClick={onConfirm}
              disabled={mutation.isPending || !hasCouriers}
            >
              {t('detail.reofferSubmit')}
            </Button>
          </>
        }
      >
        <div className="space-y-1.5">
          <Label htmlFor="reoffer-courier" className="px-[var(--form-label-padding-x)]">
            {t('detail.reofferPick')}
          </Label>
          <Select
            value={courierId}
            onValueChange={setCourierId}
            disabled={isLoading || !hasCouriers}
          >
            <SelectTrigger id="reoffer-courier" className="w-full">
              <SelectValue
                placeholder={
                  hasCouriers ? t('detail.reofferPick') : t('detail.reofferEmpty')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {couriers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} · {c.phone}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Can permission="dispatch.view">
            <Button variant="ghost" className="mt-2" asChild>
              {/* A plain link, not the router's <Link>: modals mount above the
                  router provider, where <Link> has no context and crashes. The
                  tracking route's validateSearch fills in every other param. */}
              <a
                href={`/tracking?reassign_order_id=${encodeURIComponent(String(payload.orderId))}`}
                onClick={close}
              >
                {t('detail.reofferOnMap')}
              </a>
            </Button>
          </Can>
        </div>
      </Modal>
    </Can>
  );
}
